import { useState } from 'react';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { statusLabel } from '@/shared/ui/statusLabels';

import {
  filtrosDeApoliceVazios,
  situacoesDaApolice,
  tiposDeCosseguro,
  type PolicyFilters,
} from './apolice';

/**
 * Painel "Apolices" do OnPoint (APO-028 a APO-045), com as secoes Dados da apolice e
 * Dados dos envolvidos, "Limpar filtro" e "Aplicar filtro".
 *
 * Os filtros so valem ao aplicar: a lista consulta o servidor uma vez, com todos. Grande
 * risco, Monitoramento, Retoma de objeto e Nome do corretor sao so da seguradora, como
 * no OnPoint: a corretora ja ve so as dela.
 */
export function PolicyFiltersDialog({
  open,
  value,
  onApply,
  onClose,
  brokerScoped,
}: {
  open: boolean;
  value: PolicyFilters;
  onApply: (filtros: PolicyFilters) => void;
  onClose: () => void;
  brokerScoped: boolean;
}) {
  const [rascunho, setRascunho] = useState<PolicyFilters>(value);
  const [aberto, setAberto] = useState(open);
  if (open !== aberto) {
    setAberto(open);
    if (open) setRascunho(value);
  }

  function campo<K extends keyof PolicyFilters>(chave: K, valor: PolicyFilters[K]) {
    setRascunho((atual) => ({ ...atual, [chave]: valor }));
  }

  // "Atencao: Selecione tambem a data inicial para filtrar por periodo." (APO-033).
  const semInicio = rascunho.end_date !== '' && rascunho.start_date === '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Apolices"
      description="Filtros da lista de apolices."
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => setRascunho(filtrosDeApoliceVazios)}
          >
            Limpar filtro
          </Button>
          <Button disabled={semInicio} onClick={() => onApply(rascunho)}>
            Aplicar filtro
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <section className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Dados da apolice
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Numero da apolice"
              placeholder="Digite o no da apolice"
              value={rascunho.number}
              onChange={(event) => campo('number', event.target.value)}
            />
            <Select
              label="Status da apolice"
              placeholder="Selecionar"
              value={rascunho.status}
              options={situacoesDaApolice.map((situacao) => ({
                value: situacao,
                label: statusLabel('apolice', situacao),
              }))}
              onChange={(event) =>
                campo('status', event.target.value as PolicyFilters['status'])
              }
            />
            <Field
              label="Data inicial"
              type="date"
              value={rascunho.start_date}
              onChange={(event) => campo('start_date', event.target.value)}
            />
            <Field
              label="Data final"
              type="date"
              value={rascunho.end_date}
              onChange={(event) => campo('end_date', event.target.value)}
            />
            <Select
              label="Tipo de cosseguro"
              placeholder="Selecionar"
              value={rascunho.coinsurance_type}
              options={tiposDeCosseguro}
              onChange={(event) =>
                campo(
                  'coinsurance_type',
                  event.target.value as PolicyFilters['coinsurance_type'],
                )
              }
            />
          </div>
          {semInicio ? (
            <Alert tone="warning" title="Atencao">
              Selecione tambem a data inicial para filtrar por periodo.
            </Alert>
          ) : null}
          {brokerScoped ? null : (
            <div className="flex flex-wrap gap-4">
              <Caixa
                rotulo="Grande risco"
                valor={rascunho.big_risk}
                onChange={(valor) => campo('big_risk', valor)}
              />
              <Caixa
                rotulo="Monitoramento"
                valor={rascunho.monitoring}
                onChange={(valor) => campo('monitoring', valor)}
              />
              <Caixa
                rotulo="Retoma de objeto (Step-in)"
                valor={rascunho.step_in}
                onChange={(valor) => campo('step_in', valor)}
              />
            </div>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Dados dos envolvidos
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Nome do tomador"
              placeholder="Digite"
              value={rascunho.policyholder_name}
              onChange={(event) => campo('policyholder_name', event.target.value)}
            />
            <Field
              label="Documento do tomador"
              placeholder="Digite"
              inputMode="numeric"
              value={rascunho.policyholder_document}
              onChange={(event) => campo('policyholder_document', event.target.value)}
            />
            <Field
              label="Nome do segurado"
              placeholder="Digite"
              value={rascunho.insured_name}
              onChange={(event) => campo('insured_name', event.target.value)}
            />
            <Field
              label="Documento do segurado"
              placeholder="Digite"
              inputMode="numeric"
              value={rascunho.insured_document}
              onChange={(event) => campo('insured_document', event.target.value)}
            />
            {brokerScoped ? null : (
              <Field
                label="Nome do corretor"
                placeholder="Digite"
                value={rascunho.broker_name}
                onChange={(event) => campo('broker_name', event.target.value)}
              />
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}

function Caixa({
  rotulo,
  valor,
  onChange,
}: {
  rotulo: string;
  valor: boolean;
  onChange: (valor: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700">
      <input
        type="checkbox"
        checked={valor}
        onChange={(event) => onChange(event.target.checked)}
        className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
      />
      {rotulo}
    </label>
  );
}
